<template>
  <div class="rich-editor">
    <div class="toolbar" v-if="editor">
      <button type="button" @mousedown.prevent="editor.commands.bold()"
        :class="{ active: isMarkActive('bold') }"><b>B</b></button>

      <button type="button" @mousedown.prevent="editor.commands.italic()"
        :class="{ active: isMarkActive('italic') }"><i>I</i></button>

      <button type="button" @mousedown.prevent="editor.commands.underline()"
        :class="{ active: isMarkActive('underline') }"><u>U</u></button>

      <button type="button" @mousedown.prevent="toggleList('bullet_list')"
        :class="{ active: isNodeActive('bullet_list') }">• Lista</button>

      <button type="button" @mousedown.prevent="toggleList('ordered_list')"
        :class="{ active: isNodeActive('ordered_list') }">1. Lista</button>

      <!-- <select @mousedown.stop @change="setFontSize($event.target.value)">
        <option value="">Tamanho</option>
        <option value="12px">12</option>
        <option value="14px">14</option>
        <option value="16px">16</option>
        <option value="18px">18</option>
        <option value="24px">24</option>
      </select> -->

      <input
        type="color"
        @mousedown="saveSelection"
        @change="setColor($event.target.value)"
        title="Cor do texto"
      />
    </div>

    <editor-content :editor="editor" class="editor-content" />
    <div class="editor-footer">
      <span :class="['line-counter', isNearLimit ? 'limit' : '', isAtLimit ? 'limit' : '']">
        {{ lineCount }} / {{ maxLines }} linhas
        &nbsp;·&nbsp;
        {{ charCount }} / {{ maxChars }} caracteres
      </span>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from 'tiptap'
import {
  Bold, Italic, Underline,
  BulletList, OrderedList, ListItem,
  History,
} from 'tiptap-extensions'
import ColorMark from './ColorMark'
//import FontSizeMark from './FontSizeMark'

const LINE_HEIGHT_PX = 20    // font-size 14px + line-height 1.4
const MAX_HEIGHT_PX = 880   // ~34 linhas no PDF
const MAX_CHARS = 2500

export default {
  name: 'RichTextEditor',
  components: { EditorContent },
  props: {
    value: { type: String, default: '' }
  },
  data() {
    return {
      editor: null,
      savedSelection: null,
      lastValidHTML: '',
      lastValidHeight: 0,
      lineCount: 0,
      isAtLimit: false,
      charCount: 0,
    }
  },
   computed: {
    maxLines() {
      return Math.floor(MAX_HEIGHT_PX / LINE_HEIGHT_PX)
    },
    maxChars() {
      return MAX_CHARS
    },
    isNearLimit() {
      return this.lineCount >= this.maxLines && !this.isAtLimit
    },
  },
  mounted() {
    this.lastValidHTML = this.value || '';

    this.editor = new Editor({
      content: this.value || '',
      extensions: [
        new Bold(),
        new Italic(),
        new Underline(),
        new BulletList(),
        new OrderedList(),
        new ListItem(),
        new History(),
        new ColorMark(),
        //new FontSizeMark(),
      ],
      onUpdate: ({ getHTML }) => {
        this.$nextTick(() => {
          this.checkAndEnforce(getHTML())
        })
      },
    })
  },
  beforeDestroy() {
    if (this.editor) this.editor.destroy()
  },
  watch: {
    value(newVal) {
      const currentHTML = this.editor.getHTML()
      if (newVal !== currentHTML) {
        this.editor.setContent(newVal || '')
        this.lastValidHTML = newVal || ''
      }
    }
  },
  methods: {
    checkAndEnforce(currentHTML) {
      const editorEl = this.$el.querySelector('.ProseMirror')
      if (!editorEl) return

      // Extrai texto puro para contar caracteres (sem contar as tags HTML)
      const plainText = editorEl.innerText || editorEl.textContent || ''
      const charCount = plainText.length

      const currentHeight = editorEl.scrollHeight
      this.lineCount = Math.ceil(currentHeight / LINE_HEIGHT_PX)
      this.charCount = charCount

      const heightExceeded = currentHeight > MAX_HEIGHT_PX
      const charsExceeded = charCount > MAX_CHARS

      if (heightExceeded || charsExceeded) {
        // Ultrapassou — reverte para o último estado válido
        this.isAtLimit = true
        this.editor.setContent(this.lastValidHTML)

        // Restaura o cursor para o final do conteúdo
        this.$nextTick(() => {
          this.editor.view.dispatch(
            this.editor.state.tr.setSelection(
              this.editor.state.selection.constructor.atEnd(this.editor.state.doc)
            )
          )
          this.editor.focus()
        })
      } else {
        // Dentro do limite — salva como último estado válido
        this.isAtLimit = false
        this.lastValidHTML = currentHTML
        this.$emit('input', currentHTML)
      }
    },

    isMarkActive(markName) {
      if (!this.editor) return false
      const { from, $from, to, empty } = this.editor.state.selection
      const type = this.editor.state.schema.marks[markName]
      if (!type) return false
      if (empty) return !!type.isInSet(this.editor.state.storedMarks || $from.marks())
      return this.editor.state.doc.rangeHasMark(from, to, type)
    },
    isNodeActive(nodeName) {
      if (!this.editor) return false
      const { $from } = this.editor.state.selection
      const type = this.editor.state.schema.nodes[nodeName]
      if (!type) return false
      for (let d = $from.depth; d >= 0; d--) {
        if ($from.node(d).type === type) return true
      }
      return false
    },
    toggleList(listType) {
      if (listType === 'bullet_list') {
        this.editor.commands.bullet_list()
      } else {
        this.editor.commands.ordered_list()
      }
    },
    saveSelection() {
      if (this.editor) this.savedSelection = this.editor.state.selection
    },

    // Aplica cor via mark customizada — sem execCommand
    setColor(color) {
      this.editor.focus()
      if (this.savedSelection) {
        const transaction = this.editor.state.tr.setSelection(this.savedSelection)
        this.editor.view.dispatch(transaction)
        this.savedSelection = null
      }
      this.editor.commands.color({ color })
      this.$emit('input', this.editor.getHTML())
    },

    // Aplica font-size via mark customizada — sem execCommand
    setFontSize(size) {
      if (!size) return
      this.editor.focus()
      this.editor.commands.fontSize({ size })
      this.$emit('input', this.editor.getHTML())
    },
  },
}
</script>

<style scoped>
.toolbar {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding: 8px;
  border: 1px solid #ddd;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  background: #f8f8f8;
}
.toolbar button {
  padding: 4px 10px;
  border: 1px solid #ccc;
  border-radius: 3px;
  background: white;
  cursor: pointer;
  font-size: 14px;
}
.toolbar button.active {
  background: #e0e7ff;
  border-color: #1976D2;
}

.editor-content {
  border: 1px solid #ddd;
  border-radius: 0 0 4px 4px;
}

/* Tudo dentro do ProseMirror é acessível via /deep/ no Vue 2 */
.editor-content /deep/ .ProseMirror {
  min-height: 120px;
  padding: 12px;
  font-size: 14px;
  font-family: Arial, sans-serif;  /* mesma família do PDF */
  line-height: 1.4;        /* reduz o espaçamento entre linhas */
  outline: none;           /* remove a borda preta de foco */
}

.editor-content /deep/ .ProseMirror p {
  margin: 0 0 4px 0;       /* reduz o espaço entre parágrafos */
}

.editor-content /deep/ .ProseMirror ul {
  list-style: disc;
  padding-left: 20px;
  margin: 4px 0;
}

.editor-content /deep/ .ProseMirror ol {
  list-style: decimal;
  padding-left: 20px;
  margin: 4px 0;
}

.editor-content /deep/ .ProseMirror li {
  margin-bottom: 2px;
}

/* Remove o foco azul/preto em qualquer estado */
.editor-content /deep/ .ProseMirror:focus {
  outline: none;
  box-shadow: none;
}

/* parágrafo vazio mantém altura no editor */
.editor-content /deep/ .ProseMirror p:empty {
  min-height: 1em;
}

/* alternativa: força br em parágrafo vazio via CSS */
.editor-content /deep/ .ProseMirror p:empty::after {
  content: '\00a0';   /* non-breaking space — força altura sem mostrar nada */
}

.editor-footer {
  display: flex;
  justify-content: flex-end;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  background: #f8f8f8;
}

.line-counter {
  font-size: 11px;
  color: #999;
  transition: color 0.2s;
}

.line-counter.limit {
  color: #d32f2f;
  font-weight: 500;
}
</style>